import classNames from 'classnames';
import moment from 'moment';
import React, { Fragment, useEffect, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { makeStyles } from '@mui/styles';

import { EmployeeLicenseStatus, States } from '@vestahealthcare/common/enums';
import { translate } from '@vestahealthcare/common/i18n';
import { EmployeeLicense } from '@vestahealthcare/common/models';

import {
  Colors,
  DateTimePicker,
  IconButton,
  Label,
  Select,
  TextInput,
} from 'styleguide-v2';

import { excludeOtherValues } from 'dash/src/utils/dataUtils';

type Props = {
  licenses: EmployeeLicense[];
  onChange: (pes: EmployeeLicense[]) => void;
  showCompact?: boolean;
  submitted?: boolean;
};

export const COMPACT = { value: 'COMPACT' };
export const UNKNOWN = { value: 'UNKNOWN' };

const useStyles = makeStyles({
  addIcon: {
    marginTop: '-0.25rem',
  },
  addLink: {
    color: Colors.textGreen,
    fontSize: '0.875em',
    marginLeft: '0.5rem',
    textDecoration: 'underline',

    '&:hover': {
      color: Colors.iconGreen,
    },
  },
  firstIcon: {
    '&&': {
      marginTop: '2.25rem',
    },
  },
  icon: {
    '&&': {
      padding: 0,
      marginTop: 0,
    },
  },
  marginDate: {
    marginTop: '0.5rem',
  },
});

export const EmployeLicensesEdit = ({
  licenses: paramLicenses,
  onChange,
  showCompact,
  submitted,
}: Props) => {
  const styles = useStyles();

  const [licenses, setLicenses] = useState<EmployeeLicense[]>(
    paramLicenses || [new EmployeeLicense({})],
  );

  useEffect(() => setLicenses(paramLicenses || [new EmployeeLicense({})]), [
    paramLicenses,
  ]);

  return (
    <>
      {licenses?.map((license, idx) => (
        <Fragment
          key={`employee-license-${license.state?.value || 'compact'}-${idx}`}
        >
          {idx === 0 && (
            <Label className="grid-span-12">
              <span className="flex middle" style={{ height: '17.14px' }}>
                {translate('employees.modal.stateLicensures')}{' '}
                <button
                  className={classNames('flex middle', styles.addLink)}
                  onClick={(evt) => {
                    evt.preventDefault();
                    onChange([...(licenses || []), new EmployeeLicense({})]);
                  }}
                >
                  <AddIcon className={styles.addIcon} fontSize="small" />
                  {translate('employees.modal.addStateLicensure')}
                </button>
              </span>
            </Label>
          )}
          <Select
            className="grid-span-3"
            disableClearable
            error={submitted && license.state === undefined}
            getItemLabel={(state: States) =>
              state === COMPACT ? 'Compact' : state?.value
            }
            filterOptions={(
              data: States[],
              { inputValue }: { inputValue: string },
            ) =>
              data.filter(
                (item: States) =>
                  item?.value
                    ?.toLocaleLowerCase()
                    ?.includes(inputValue?.toLocaleLowerCase()) ||
                  item
                    ?.toString()
                    ?.toLocaleLowerCase()
                    ?.includes(inputValue?.toLocaleLowerCase()),
              )
            }
            items={excludeOtherValues(
              [showCompact ? COMPACT : undefined, ...States.asArray].filter(
                Boolean,
              ) as States[],
              licenses.map(({ state }) => state) as States[],
              license.state,
            )}
            placeholder={translate('employees.modal.state')}
            onChange={(state: States) => {
              licenses[idx].state = state;
              onChange([...licenses]);
            }}
            value={license.state === null ? COMPACT : license.state}
            size="small"
          />
          <Select
            className="grid-span-8"
            disableClearable
            disabled={!showCompact && license.state === COMPACT}
            error={submitted && !license.status}
            getItemLabel={(status: EmployeeLicenseStatus) => status?.toString()}
            items={EmployeeLicenseStatus.asEditableArray}
            placeholder={translate('employees.modal.status')}
            onChange={(status: EmployeeLicenseStatus) => {
              licenses[idx].status = status;
              onChange([...licenses]);
            }}
            value={license.status}
            size="small"
          />
          <IconButton
            className={classNames(
              styles.icon,
              idx === 0 && styles.firstIcon,
              'grid-span-1',
            )}
            size="small"
            onClick={() => {
              if (licenses && licenses.length < 2) {
                onChange([new EmployeeLicense({})]);
              } else {
                licenses?.splice(idx, 1);
                onChange([...licenses]);
              }
            }}
          >
            <DeleteOutlineIcon fontSize="large" />
          </IconButton>
          <TextInput
            className="grid-span-4"
            onChange={(licenseNumber?: string) => {
              licenses[idx].licenseNumber = licenseNumber || '';
              onChange([...licenses]);
            }}
            value={license.licenseNumber}
            placeholder={translate('employees.modal.licenseNumber')}
            size="small"
          />
          <DateTimePicker
            className={classNames('grid-span-4', styles.marginDate)}
            maxDate={moment().toDate()}
            onChange={(issuedAt?: Date) => {
              licenses[idx].issuedAt = issuedAt
                ? moment(issuedAt.getTime())
                : undefined;
              onChange([...licenses]);
            }}
            placeholder={translate('employees.modal.issuedAt')}
            value={licenses[idx].issuedAt?.toDate()}
            size="small"
          />
          <DateTimePicker
            className={classNames('grid-span-4', styles.marginDate)}
            error={
              submitted &&
              license.status === EmployeeLicenseStatus.EXPIRED &&
              !license.expiresAt
            }
            onChange={(expiresAt?: Date) => {
              licenses[idx].expiresAt = expiresAt
                ? moment(expiresAt.getTime())
                : undefined;
              onChange([...licenses]);
            }}
            placeholder={translate('employees.modal.expiresAt')}
            value={licenses[idx].expiresAt?.toDate()}
            size="small"
          />

          {idx !== licenses?.length - 1 && (
            <hr className="separator grid-span-12" />
          )}
        </Fragment>
      ))}
    </>
  );
};
